import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { Kanban, DollarSign, MapPin, ListChecks, Filter } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { useEmpresas } from '@/hooks/useEmpresa';
import {
  useGestaoLicitacoesPipelineGeral,
  useGestaoLicitacoesResumoGeral,
  useMoverEtapaPipelineGeral,
} from '@/hooks/useGestaoLicitacoes';
import { AdicionarAoPipelineModal } from '@/components/gestao-licitacoes/AdicionarAoPipelineModal';
import { LicitacaoDetalhesModal } from '@/components/gestao-licitacoes/LicitacaoDetalhesModal';
import { PIPELINE_ETAPAS, handleDragEnd } from '@/lib/pipelineKanban';
import { CATEGORIAS, UFS_BR } from '@/lib/licitacoesOpcoes';
import type { PipelineEntradaGeral, PipelineEtapa } from '@/types/gestaoLicitacoes';
import { useViewModeStore } from '@/stores/view-mode';

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const COLUNAS: Array<{ etapa: PipelineEtapa; label: string }> = [
  { etapa: 'captacao', label: 'Captação' },
  { etapa: 'em_analise', label: 'Em análise' },
  { etapa: 'em_disputa', label: 'Em disputa' },
  { etapa: 'ganha', label: 'Ganha' },
  { etapa: 'perdida', label: 'Perdida' },
];

const selectClassName =
  'flex w-full rounded-lg border border-ds-border bg-ds-surface2 px-3 py-2.5 text-sm text-ds-text-primary ' +
  'focus:outline-none focus:ring-2 focus:ring-ds-brand/50 focus:border-ds-brand/50 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString('pt-BR');

interface IndicadorCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
}

function IndicadorCard({ title, value, icon: Icon }: IndicadorCardProps) {
  return (
    <Card variant="hover">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-ds-text-secondary">{title}</CardTitle>
        <div className="w-10 h-10 rounded-lg bg-ds-surface2 flex items-center justify-center">
          <Icon className="h-5 w-5 text-ds-brand" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-ds-text-primary">{value}</div>
      </CardContent>
    </Card>
  );
}

function LicitacaoCard({ entrada, onClick }: { entrada: PipelineEntradaGeral; onClick: (entrada: PipelineEntradaGeral) => void }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `${entrada.empresaId}:${entrada.licitacaoId}`,
  });

  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);

  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined;

  const handleClick = (event: React.MouseEvent) => {
    const start = pointerDownPos.current;
    if (start) {
      const dx = Math.abs(event.clientX - start.x);
      const dy = Math.abs(event.clientY - start.y);
      if (dx > 5 || dy > 5) return;
    }
    onClick(entrada);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onPointerDownCapture={(event) => {
        pointerDownPos.current = { x: event.clientX, y: event.clientY };
      }}
      onClick={handleClick}
      className={cn(
        'rounded-lg border border-ds-border bg-ds-surface2 p-3 mb-2 cursor-grab active:cursor-grabbing touch-none',
        isDragging && 'opacity-50'
      )}
    >
      <p className="text-[10px] font-semibold text-ds-info uppercase tracking-wide truncate">{entrada.empresaNome}</p>
      <p className="text-sm font-medium text-ds-text-primary line-clamp-2">{entrada.licitacao.objeto}</p>
      {entrada.licitacao.orgao && (
        <p className="text-xs text-ds-text-secondary mt-1">{entrada.licitacao.orgao}</p>
      )}
      <div className="flex items-center justify-between mt-2 gap-2">
        {entrada.licitacao.uf && <Badge size="sm">{entrada.licitacao.uf}</Badge>}
        {entrada.licitacao.valorEstimado != null && (
          <span className="text-xs font-semibold text-ds-success">
            {formatCurrency(Number(entrada.licitacao.valorEstimado))}
          </span>
        )}
      </div>
      {entrada.licitacao.prazoAbertura && (
        <p className="text-xs text-ds-text-secondary mt-1">{formatDate(entrada.licitacao.prazoAbertura)}</p>
      )}
    </div>
  );
}

interface ColunaKanbanProps {
  etapa: PipelineEtapa;
  label: string;
  entradas: PipelineEntradaGeral[];
  onCardClick: (entrada: PipelineEntradaGeral) => void;
}

function ColunaKanban({ etapa, label, entradas, onCardClick }: ColunaKanbanProps) {
  const { setNodeRef, isOver } = useDroppable({ id: etapa });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex-1 min-w-[240px] rounded-xl border border-ds-border bg-ds-surface p-3',
        isOver && 'border-ds-brand/50 bg-ds-brand-subtle/30'
      )}
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <h3 className="text-sm font-semibold text-ds-text-primary">{label}</h3>
        <Badge size="sm">{entradas.length}</Badge>
      </div>
      <div className="min-h-[80px]">
        {entradas.length === 0 ? (
          <p className="text-xs text-ds-text-secondary text-center py-6">Nenhuma licitação nesta etapa</p>
        ) : (
          entradas.map((entrada) => (
            <LicitacaoCard
              key={`${entrada.empresaId}:${entrada.licitacaoId}`}
              entrada={entrada}
              onClick={onCardClick}
            />
          ))
        )}
      </div>
    </div>
  );
}

function GestaoLicitacoes() {
  const [searchParams] = useSearchParams();
  const empresaFromUrl = parseInt(searchParams.get('empresa') || '', 10);
  const { mode, previewEmpresaId } = useViewModeStore();
  const empresaEmContexto = mode === 'empresa' && previewEmpresaId !== null
    ? previewEmpresaId
    : (empresaFromUrl || null);

  const [modalAberto, setModalAberto] = useState(false);
  const [detalhesEntrada, setDetalhesEntrada] = useState<PipelineEntradaGeral | null>(null);
  const [filtrosAbertos, setFiltrosAbertos] = useState(!!empresaFromUrl);
  const [empresaIds, setEmpresaIds] = useState<number[]>(empresaFromUrl ? [empresaFromUrl] : []);
  const [uf, setUf] = useState('');
  const [categoria, setCategoria] = useState('');
  const [modalidade, setModalidade] = useState('');

  useEffect(() => {
    if (!empresaEmContexto) return;
    setEmpresaIds([empresaEmContexto]);
    setFiltrosAbertos(true);
  }, [empresaEmContexto]);

  const empresaIdsEfetivos = empresaEmContexto ? [empresaEmContexto] : empresaIds;

  const filtros = {
    empresaIds: empresaIdsEfetivos.length > 0 ? empresaIdsEfetivos : undefined,
    uf: uf || undefined,
    categoria: categoria || undefined,
    modalidade: modalidade || undefined,
  };

  const { data: empresas } = useEmpresas();
  const { data: pipeline, isLoading: pipelineLoading } = useGestaoLicitacoesPipelineGeral(filtros);
  const { data: resumo } = useGestaoLicitacoesResumoGeral(filtros);
  const moverEtapa = useMoverEtapaPipelineGeral(filtros);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const entradasPorEtapa = useMemo(() => {
    const grupos: Record<PipelineEtapa, PipelineEntradaGeral[]> = {
      captacao: [],
      em_analise: [],
      em_disputa: [],
      ganha: [],
      perdida: [],
    };
    (pipeline || []).forEach((entrada) => {
      grupos[entrada.etapa].push(entrada);
    });
    return grupos;
  }, [pipeline]);

  const toggleEmpresa = (id: number) => {
    setEmpresaIds((atual) => (atual.includes(id) ? atual.filter((e) => e !== id) : [...atual, id]));
  };

  const limparFiltros = () => {
    setEmpresaIds([]);
    setUf('');
    setCategoria('');
    setModalidade('');
  };

  const temFiltrosAtivos = empresaIdsEfetivos.length > 0 || !!uf || !!categoria || !!modalidade;

  const onDragEnd = (event: DragEndEvent) => {
    handleDragEnd(event, ({ activeId, etapa }) => {
      const [empresaIdStr, licitacaoIdStr] = activeId.split(':');
      const empresaId = parseInt(empresaIdStr, 10);
      const licitacaoId = parseInt(licitacaoIdStr, 10);
      if (!empresaId || !licitacaoId) return;
      moverEtapa.mutate({ empresaId, licitacaoId, etapa });
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ds-text-primary">Gestão da Licitação</h1>
          <p className="text-sm text-ds-text-secondary mt-1">
            {empresaEmContexto ? 'Acompanhe o funil comercial da empresa selecionada.' : 'Acompanhe o funil comercial das licitações de todos os clientes.'}
          </p>
        </div>
        <Button onClick={() => setModalAberto(true)}>
          Adicionar à gestão
        </Button>
      </div>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setFiltrosAbertos((v) => !v)}
          >
            <Filter className="h-4 w-4 mr-1.5" />
            Filtros
            {temFiltrosAtivos && (
              <span className="ml-1.5 inline-flex items-center justify-center rounded-full bg-ds-success/20 text-ds-success text-[10px] font-bold w-4 h-4">
                {[empresaIds.length > 0, !!uf, !!categoria, !!modalidade].filter(Boolean).length}
              </span>
            )}
          </Button>

          {filtrosAbertos && (
            <div className="rounded-lg border border-ds-border bg-ds-surface2/60 p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-ds-text-secondary" htmlFor="gestao-filtro-uf">UF</label>
                  <select
                    id="gestao-filtro-uf"
                    className={selectClassName}
                    value={uf}
                    onChange={(e) => setUf(e.target.value)}
                  >
                    <option value="">Todos os estados</option>
                    {UFS_BR.map((sigla) => (
                      <option key={sigla} value={sigla}>{sigla}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-ds-text-secondary" htmlFor="gestao-filtro-categoria">Nicho (categoria)</label>
                  <select
                    id="gestao-filtro-categoria"
                    className={selectClassName}
                    value={categoria}
                    onChange={(e) => setCategoria(e.target.value)}
                  >
                    <option value="">Todas as categorias</option>
                    {CATEGORIAS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-ds-text-secondary" htmlFor="gestao-filtro-modalidade">Modalidade</label>
                  <input
                    id="gestao-filtro-modalidade"
                    className={selectClassName}
                    placeholder="Ex: Pregão Eletrônico"
                    value={modalidade}
                    onChange={(e) => setModalidade(e.target.value)}
                  />
                </div>
              </div>

              {!empresaEmContexto && empresas && empresas.length > 0 && (
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-ds-text-secondary">Clientes / Empresas</label>
                  <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                    {empresas.map((empresa) => (
                      <label
                        key={empresa.id}
                        className="flex items-center gap-1.5 cursor-pointer text-xs text-ds-text-primary bg-ds-surface border border-ds-border rounded-full px-2.5 py-1"
                      >
                        <input
                          type="checkbox"
                          checked={empresaIds.includes(empresa.id)}
                          onChange={() => toggleEmpresa(empresa.id)}
                          className="accent-ds-success w-3.5 h-3.5"
                        />
                        {empresa.razao_social}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {temFiltrosAtivos && (
                <div className="flex justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={limparFiltros}>
                    Limpar filtros
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <IndicadorCard title="Total em gestão" value={resumo?.totalEmGestao ?? 0} icon={Kanban} />
        <IndicadorCard
          title="Valor total estimado"
          value={formatCurrency(resumo?.valorTotalEstimado ?? 0)}
          icon={DollarSign}
        />
        <IndicadorCard title="Estados" value={resumo?.porUf.length ?? 0} icon={MapPin} />
        <IndicadorCard title="Etapas do funil" value={PIPELINE_ETAPAS.length} icon={ListChecks} />
      </div>

      {pipelineLoading ? (
        <Card>
          <p className="text-sm text-ds-text-secondary text-center py-8">Carregando...</p>
        </Card>
      ) : (pipeline || []).length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-sm text-ds-text-secondary mb-4">
              Nenhuma licitação em gestão ainda.
            </p>
            <Button onClick={() => setModalAberto(true)}>Adicionar à gestão</Button>
          </div>
        </Card>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {COLUNAS.map(({ etapa, label }) => (
              <ColunaKanban
                key={etapa}
                etapa={etapa}
                label={label}
                entradas={entradasPorEtapa[etapa]}
                onCardClick={setDetalhesEntrada}
              />
            ))}
          </div>
        </DndContext>
      )}

      <AdicionarAoPipelineModal
        isOpen={modalAberto}
        empresas={empresas || []}
        defaultEmpresaId={empresaIdsEfetivos.length === 1 ? empresaIdsEfetivos[0] : undefined}
        pipeline={pipeline || []}
        onClose={() => setModalAberto(false)}
      />

      <LicitacaoDetalhesModal entrada={detalhesEntrada} onClose={() => setDetalhesEntrada(null)} />
    </div>
  );
}

export { GestaoLicitacoes };
